import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import type { ApiError } from '../types/common';

// Types
export interface EncryptedSessionNote {
  id: number;
  bookingId: number;
  therapistId: number;
  encryptedContent: string;  // Base64-encoded ciphertext
  iv: string;                 // Base64-encoded initialization vector
  version: number;            // Encryption version
  createdAt: string;
  updatedAt: string;
}

export interface SessionNoteWithBooking extends EncryptedSessionNote {
  booking: {
    id: number;
    consumerId: number;
    slotDate: string;
    slotTime: string;
    sessionType: string;
    status: string;
    consumer: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
}

export interface SaveNoteRequest {
  encryptedContent: string;
  iv: string;
  version: number;
}

// Query keys
const noteKeys = {
  all: ['session-notes'] as const,
  byBooking: (bookingId: number) => [...noteKeys.all, 'booking', bookingId] as const,
  therapistNotes: () => [...noteKeys.all, 'therapist'] as const,
};

// API functions
async function getSessionNote(bookingId: number): Promise<EncryptedSessionNote> {
  const response = await apiClient.get<EncryptedSessionNote>(`/notes/sessions/${bookingId}/notes`);
  return response.data;
}

async function saveSessionNote(bookingId: number, data: SaveNoteRequest): Promise<EncryptedSessionNote> {
  const response = await apiClient.post<EncryptedSessionNote>(`/notes/sessions/${bookingId}/notes`, data);
  return response.data;
}

async function deleteSessionNote(bookingId: number): Promise<void> {
  await apiClient.delete(`/notes/sessions/${bookingId}/notes`);
}

async function getTherapistNotes(): Promise<{ notes: SessionNoteWithBooking[]; total: number }> {
  const response = await apiClient.get<{ notes: SessionNoteWithBooking[]; total: number }>('/notes/therapist/notes');
  return response.data;
}

// Hooks

/**
 * Get encrypted session note for a specific booking
 */
export function useSessionNote(bookingId: number, options?: { enabled?: boolean }) {
  return useQuery<EncryptedSessionNote, ApiError>({
    queryKey: noteKeys.byBooking(bookingId),
    queryFn: () => getSessionNote(bookingId),
    enabled: options?.enabled ?? true,
    retry: (failureCount, error) => {
      // Don't retry on 404 (note doesn't exist yet)
      if (error.code === '404') {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Save or update a session note
 */
export function useSaveSessionNote() {
  const queryClient = useQueryClient();

  return useMutation<EncryptedSessionNote, ApiError, { bookingId: number; data: SaveNoteRequest }>({
    mutationFn: ({ bookingId, data }) => saveSessionNote(bookingId, data),
    onSuccess: (data, variables) => {
      // Update the cache with the new note
      queryClient.setQueryData(noteKeys.byBooking(variables.bookingId), data);

      // Invalidate therapist notes list
      queryClient.invalidateQueries({ queryKey: noteKeys.therapistNotes() });
    },
    onError: (error) => {
      console.error('Failed to save session note:', error);
    },
  });
}

/**
 * Delete a session note
 */
export function useDeleteSessionNote() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, number>({
    mutationFn: (bookingId) => deleteSessionNote(bookingId),
    onSuccess: (_, bookingId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: noteKeys.byBooking(bookingId) });

      // Invalidate therapist notes list
      queryClient.invalidateQueries({ queryKey: noteKeys.therapistNotes() });
    },
    onError: (error) => {
      console.error('Failed to delete session note:', error);
    },
  });
}

/**
 * Get all notes for the authenticated therapist
 */
export function useTherapistNotes() {
  return useQuery<{ notes: SessionNoteWithBooking[]; total: number }, ApiError>({
    queryKey: noteKeys.therapistNotes(),
    queryFn: getTherapistNotes,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}
